import { SMB } from '../../constants/ImageData';
import { buildAuditExportRows, fetchAndPrepareAuditSheetRows } from '../../pages/audit_sheet/components/transformAuditData';

export const downloadAsExcel = async (data) => {
  const ExcelJS = (await import('exceljs')).default ?? (await import('exceljs'));
  const wb = new ExcelJS.Workbook();
  wb.creator  = 'CodeQuick Audit';
  wb.created  = new Date();

  // ── Shared style helpers ──────────────────────────────────────────────────

  const FONT = 'Arial';

  // Fill helper
  const fill = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + hex } });

  // Border helpers
  const thinBorder = (hex = '000000') => ({ style: 'thin', color: { argb: 'FF' + hex } });
  const allBorders = (hex = '000000') => ({
    top: thinBorder(hex), bottom: thinBorder(hex),
    left: thinBorder(hex), right: thinBorder(hex),
  });
  const medBorder  = (hex) => ({ style: 'medium', color: { argb: 'FF' + hex } });
  const allMedBorders = (hex) => ({
    top: medBorder(hex), bottom: medBorder(hex),
    left: medBorder(hex), right: medBorder(hex),
  });

  // Apply full style to a cell
  const styleCell = (cell, {
    fontColor = '000000', fontSize = 10, bold = false, italic = false,
    bgColor = null, hAlign = 'left', vAlign = 'top',
    wrapText = true, borders = null,
  } = {}) => {
    cell.font      = { name: FONT, size: fontSize, bold, italic, color: { argb: 'FF' + fontColor } };
    cell.alignment = { horizontal: hAlign, vertical: vAlign, wrapText };
    if (bgColor) cell.fill = fill(bgColor);
    if (borders) cell.border = borders;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Iterate providers
  // ─────────────────────────────────────────────────────────────────────────
  for (const chart of data.charts) {
    for (const provider of chart.providers) {
      const s       = provider.provider_summary;
      const pName   = provider.provider_name;
      const safePfx = pName.replace(/[:\\\/\?\*\[\]]/g, '').substring(0, 18);
      const cqPct   = s.cq_score != null ? `${(s.cq_score).toFixed(0)}%` : '—';
      const auditPeriod = chart.batch_name || 'Audit Report';

      // ══════════════════════════════════════════════════════════════
      // SHEET 1 — Overview
      // ══════════════════════════════════════════════════════════════
      const wsOv = wb.addWorksheet(`${safePfx} - Overview`);
      wsOv.properties.defaultRowHeight = 18;

      // Column widths
      wsOv.columns = [
        { key: 'A', width: 6  },
        { key: 'B', width: 30 },
        { key: 'C', width: 22 },
        { key: 'D', width: 6  },
        { key: 'E', width: 6  },
      ];

      // Set row height
        for (let r = 1; r <= 20; r++) {
        wsOv.getRow(r).height = 18;
        }

        // Merge logo area
        wsOv.mergeCells('A1:E20');

        // Add image
        const imageId = wb.addImage({
        base64: SMB,
        extension: 'png',
        });

        // Place image
        wsOv.addImage(imageId, {
        tl: { col: 0, row: 0 },
        br: { col: 5, row: 20 },
        });

      // ── Row 21: spacer ──
      wsOv.getRow(21).height = 10;

      // ── Row 22: spacer ──
      wsOv.getRow(22).height = 10;

      // ── Row 23: Provider name (merged B23:C23) ──
      wsOv.getRow(23).height = 28;
      wsOv.mergeCells('B23:C23');
      const nameCell = wsOv.getCell('B23');
      nameCell.value = pName;
      styleCell(nameCell, {
        fontSize: 15, bold: true, fontColor: '1A1A1A',
        hAlign: 'center', vAlign: 'middle',
      });

      // ── Row 24: Audit period (merged B24:C24) ──
      wsOv.getRow(24).height = 20;
      wsOv.mergeCells('B24:C24');
      const periodCell = wsOv.getCell('B24');
      periodCell.value = auditPeriod;
      styleCell(periodCell, {
        fontSize: 11, fontColor: '555555',
        hAlign: 'center', vAlign: 'middle',
      });

      // ── Row 25: Accuracy Score label + value ──
      wsOv.getRow(25).height = 26;

      const scoreLabelCell = wsOv.getCell('B25');
      scoreLabelCell.value = 'Accuracy Score:';
      styleCell(scoreLabelCell, {
        fontSize: 11, bold: true, fontColor: 'FFFFFF',
        bgColor: '1F4E79', hAlign: 'left', vAlign: 'middle',
        borders: allBorders('B8CCE4'),
      });

      const scoreValueCell = wsOv.getCell('C25');
      scoreValueCell.value = cqPct;
      styleCell(scoreValueCell, {
        fontSize: 13, bold: true, fontColor: '1F4E79',
        bgColor: 'FFFF00', hAlign: 'center', vAlign: 'middle',
        borders: allBorders('B8CCE4'),
      });

      // ══════════════════════════════════════════════════════════════
      // SHEET 2 — ProviderScore
      // ══════════════════════════════════════════════════════════════
      const wsScore = wb.addWorksheet(`${safePfx} - ProviderScore`);
      wsScore.properties.defaultRowHeight = 18;

      wsScore.columns = [
        { key: 'A', width: 44 },
        { key: 'B', width: 10 },
        { key: 'C', width: 90 },
      ];

      // ── Header row ──
      const hdrRow = wsScore.getRow(1);
      hdrRow.height = 20;
      ['Audit Issue', 'Value', 'Comments'].forEach((label, ci) => {
        const cell = hdrRow.getCell(ci + 1);
        cell.value = label;
        styleCell(cell, {
          bold: true, bgColor: 'D9D9D9', hAlign: 'center', vAlign: 'middle',
          borders: allBorders(),
        });
      });

      // Sort: flagged first, then clean
      const sortedRows = [
        ...provider.provider_score.rows.filter((r) => r.value > 0),
        ...provider.provider_score.rows.filter((r) => r.value === 0),
      ];

      // ── Data rows ──
      sortedRows.forEach((row, i) => {
        const excelRowNum = i + 2;
        const dataRow     = wsScore.getRow(excelRowNum);
        const isFlagged   = row.value > 0;

        // Row height: taller for cells with long comments
        const commentLen = (row.comments || '').length;
        dataRow.height   = commentLen > 300 ? 120 : commentLen > 150 ? 80 : commentLen > 60 ? 45 : 18;

        const bgColor = isFlagged ? 'FFC000' : 'FFFFFF';

        // A: Audit Issue
        const cellA = dataRow.getCell(1);
        cellA.value = row.audit_issue;
        styleCell(cellA, { bgColor, vAlign: 'top', wrapText: true, borders: allBorders() });

        // B: Value
        const cellB = dataRow.getCell(2);
        cellB.value = row.value;
        styleCell(cellB, { bgColor, hAlign: 'center', vAlign: 'top', borders: allBorders() });

        // C: Comments
        const cellC = dataRow.getCell(3);
        cellC.value = row.comments || '';
        styleCell(cellC, { bgColor, vAlign: 'top', wrapText: true, borders: allBorders() });
      });

      // ── Footer ──
      const footerHeaderRowNum = sortedRows.length + 3;
      const footerDataRowNum   = sortedRows.length + 4;
      const footerTotalRowNum  = sortedRows.length + 5;

      // Footer header row (gray)
      const fhRow = wsScore.getRow(footerHeaderRowNum);
      fhRow.height = 18;
      [['A', 'Provider'], ['B', 'Total Audited'], ['C', 'TOC score']].forEach(([col, label]) => {
        const cell = fhRow.getCell(col);
        cell.value = label;
        styleCell(cell, {
          bold: true, bgColor: 'D9D9D9', hAlign: 'center', vAlign: 'middle',
          borders: allBorders(),
        });
      });

      // Footer data row (blue + yellow score)
      const fdRow = wsScore.getRow(footerDataRowNum);
      fdRow.height = 22;

      const fdA = fdRow.getCell('A');
      fdA.value  = pName;
      styleCell(fdA, { bgColor: 'BDD7EE', bold: true, vAlign: 'middle', borders: allBorders() });

      const fdB = fdRow.getCell('B');
      fdB.value  = s.total_audited;
      styleCell(fdB, { bgColor: 'BDD7EE', bold: true, hAlign: 'center', vAlign: 'middle', borders: allBorders() });

      const fdC = fdRow.getCell('C');
      fdC.value  = cqPct;
      styleCell(fdC, {
        bgColor: 'FFFF00', bold: true, fontSize: 12,
        hAlign: 'center', vAlign: 'middle', borders: allBorders(),
      });

      // Total row (blank provider, repeat audited count)
      const ftRow = wsScore.getRow(footerTotalRowNum);
      ftRow.height = 18;
      const ftA = ftRow.getCell('A'); ftA.value = ''; styleCell(ftA, { bgColor: 'BDD7EE', borders: allBorders() });
      const ftB = ftRow.getCell('B'); ftB.value = s.total_audited;
      styleCell(ftB, { bgColor: 'BDD7EE', bold: true, hAlign: 'center', vAlign: 'middle', borders: allBorders() });
      const ftC = ftRow.getCell('C'); ftC.value = ''; styleCell(ftC, { bgColor: 'BDD7EE', borders: allBorders() });

      // ══════════════════════════════════════════════════════════════
      // SHEET 3 — Provider Summary
      // ══════════════════════════════════════════════════════════════
      const wsSum = wb.addWorksheet(`${safePfx} - Summary`);
      wsSum.properties.defaultRowHeight = 18;
      wsSum.columns = [{ key: 'A', width: 140 }];

      // Row 1: Title
      const titleRow = wsSum.getRow(1);
      titleRow.height = 22;
      const titleCell = titleRow.getCell(1);
      titleCell.value = `Summary- Audit Notes: ${pName}`;
      styleCell(titleCell, { fontSize: 13, bold: true, vAlign: 'middle' });

      // Row 2: "Overall findings" subheading
      const subRow = wsSum.getRow(2);
      subRow.height = 16;
      const subCell = subRow.getCell(1);
      subCell.value = 'Overall findings';
      styleCell(subCell, { bold: true, vAlign: 'middle' });

      // Row 3: Overall findings text
      const ofRow = wsSum.getRow(3);
      ofRow.height = 40;
      const ofCell = ofRow.getCell(1);
      ofCell.value = s.overall_findings || '';
      styleCell(ofCell, { vAlign: 'top', wrapText: true });

      // Row 4: spacer
      wsSum.getRow(4).height = 8;

      // Rows 5+: MRN notes — blue rows like screenshot
      const mrnNotes = s.encounter_notes ?? [];
      mrnNotes.forEach(({ encounter, note }, i) => {
        const mrnRow = wsSum.getRow(i + 5);
        mrnRow.height = 28;
        const mrnCell = mrnRow.getCell(1);
        mrnCell.value = `Encounter #: ${encounter},  ${note}`;
        styleCell(mrnCell, {
          bold: true, fontColor: '1F3864',
          bgColor: 'DAE3F3', vAlign: 'middle', wrapText: true,
        });
      });
      // ══════════════════════════════════════════════════════════════
      // SHEET 4 — Audit Data (CPT-wise, same as "Export to CSV CPT-wise")
      // ══════════════════════════════════════════════════════════════
      const uploadId = chart.chart_pk; // chart_id === uploadId on the backend

      const auditSheetData = await fetchAndPrepareAuditSheetRows(uploadId);
      if (auditSheetData) {
        const { arrangedData, columnData, headers } = auditSheetData;

        // response IDs stay as-is (no responseAuditIssues available here).
        // To resolve display names, have the API embed them in the row directly.
        const { headers: finalHeaders, rows } = buildAuditExportRows({
          sourceData:          arrangedData,
          columnData,
          headers,
          responseAuditIssues: null,
          generateOptions:     () => [],
        });

        const wsAudit = wb.addWorksheet(`${safePfx} - Audit Data`);
        wsAudit.properties.defaultRowHeight = 18;

        // Column widths — first few columns narrow, notes/comments wide
        const colWidths = [20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 90];
        wsAudit.columns = finalHeaders.map((_, i) => ({
          width: colWidths[i] ?? 20,
        }));

        // Header row — gray like other sheets
        const hdr = wsAudit.getRow(1);
        hdr.height = 20;
        finalHeaders.forEach((label, ci) => {
          const cell = hdr.getCell(ci + 1);
          cell.value = label;
          styleCell(cell, {
            bold: true, bgColor: 'D9D9D9',
            hAlign: 'center', vAlign: 'middle',
            borders: allBorders(),
          });
        });

        // Data rows
        rows.forEach((rowArr, i) => {
          const excelRow = wsAudit.getRow(i + 2);
          excelRow.height = 18;
          rowArr.forEach((cellValue, ci) => {
            const cell = excelRow.getCell(ci + 1);
            cell.value  = cellValue ?? '';
            styleCell(cell, {
              vAlign: 'top', wrapText: true,
              borders: allBorders(),
            });
          });
        });
      }
    };
  };

  // ── Write & trigger download ──────────────────────────────────────────────
  const buffer   = await wb.xlsx.writeBuffer();
  const blob     = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url      = URL.createObjectURL(blob);
  const link     = document.createElement('a');
  link.href      = url;
  link.download  = `Audit_Report_${
    data.charts[0]?.batch_name || data.charts[0]?.chart_id || 'export'
  }.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};